import { useCallback, useEffect, useRef, useState } from 'react';
import type { Dayjs } from 'dayjs';
import { appointmentApi } from '../../services/appointmentApi';
import { scheduleApi, ScheduleException } from '../../services/scheduleApi';
import { apiRoot } from '../../services/apiInstances';
import { segmentAppointments, SegmentedAppointment } from './segments';

export type SchedView = 'week' | 'day' | 'month' | 'agenda';
export interface TechLane {
  id: number;
  name: string;
  role: string;
  // SP-1 Task 6: per-tech color (backend `user_profiles.calendar_color`,
  // mig 163) used as the ApptBlock left-edge accent. Undefined when the
  // tech has never set one — callers fall back to a neutral edge color.
  calendar_color?: string;
}

/** Resolve a technician's `calendar_color` from the loaded `techs` list by
 *  id. Returns undefined (neutral-edge fallback) when unassigned, the tech
 *  isn't in the list, or the tech has no color set. */
export function resolveTechColor(techs: TechLane[], technicianId?: number | null): string | undefined {
  if (technicianId == null) return undefined;
  return techs.find((t) => String(t.id) === String(technicianId))?.calendar_color;
}

/** Up to two uppercase initials for a display name ("Ada Amber" → "AA",
 *  "Cy" → "C"). Returns '' for anything unusable rather than throwing — this
 *  only drives a display badge. */
export function initialsFromName(name?: string | null): string {
  if (!name) return '';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
}

/** WP-01: initials for the assigned technician, resolved from the same loaded
 *  `techs` list the color comes from. Used to tell an assigned-but-COLOURLESS
 *  technician apart from a genuinely UNASSIGNED appointment — both otherwise
 *  render the identical neutral grey edge, which on Week view (where every
 *  tech's blocks share one day column) leaves the block's owner unreadable.
 *  Returns undefined when unassigned or the tech isn't in the list, so callers
 *  can fall back to whatever the appointment payload itself carries. */
export function resolveTechInitials(techs: TechLane[], technicianId?: number | null): string | undefined {
  if (technicianId == null) return undefined;
  const tech = techs.find((t) => String(t.id) === String(technicianId));
  if (!tech) return undefined;
  return initialsFromName(tech.name) || undefined;
}

// Multi-day appointments are keyed server-side by the PARENT's start_time —
// AppointmentModel.findByDateRangeAndShop filters on that column, not on any
// individual day's segment. A Day-view query scoped to just `anchor` misses a
// continuation segment landing on `anchor` whose parent started earlier (e.g.
// anchor = Fri, parent started Thu). Widen the START of the Day-view fetch
// back far enough to catch any parent that could still be rolling into
// `anchor` (multi-day span is capped at 5 working days — 10 calendar days is
// a safe margin), while leaving `end_date` at `anchor` so a parent that
// STARTS on `anchor` (start_time already in range) is unaffected. DayGrid
// already buckets strictly by `segment.date === dateStr`, so the extra
// fetched days never leak into the rendered view.
const DAY_VIEW_LOOKBACK_DAYS = 10;

export function rangeFor(view: SchedView, anchor: Dayjs): { start_date: string; end_date: string } {
  const fmt = (d: Dayjs) => d.format('YYYY-MM-DD');
  // Agenda is a day-to-day list (SP-1 Task 8), so it uses the same
  // single-day fetch strategy as Day view — not the week range — so
  // navigating day-by-day shows exactly that day's work (incl. multi-day
  // continuations rolling in from an earlier day).
  if (view === 'day' || view === 'agenda') {
    return { start_date: fmt(anchor.subtract(DAY_VIEW_LOOKBACK_DAYS, 'day')), end_date: fmt(anchor) };
  }
  if (view === 'month') return { start_date: fmt(anchor.startOf('month')), end_date: fmt(anchor.endOf('month')) };
  // week
  return { start_date: fmt(anchor.startOf('week')), end_date: fmt(anchor.endOf('week')) };
}

export function useSchedulingData(view: SchedView, anchor: Dayjs, technicianId?: number) {
  const [appts, setAppts] = useState<SegmentedAppointment[]>([]);
  const [techs, setTechs] = useState<TechLane[]>([]);
  const [exceptions, setExceptions] = useState<ScheduleException[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // WP-01: every reschedule path (drag, and both edit modals) ends in reload(),
  // so two moves in quick succession put two fetches in flight at once. Without
  // a guard the response that lands LAST wins, and a slow earlier request can
  // overwrite the fresher one — leaving the calendar showing pre-move
  // technicians, and therefore pre-move technician colours, indefinitely. This
  // makes the request most recently ISSUED authoritative regardless of the order
  // responses arrive in, and drops superseded results (successes and failures
  // alike) instead of letting them write state.
  const loadSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    const isCurrent = () => seq === loadSeq.current;
    setLoading(true); setError(null);
    try {
      const { start_date, end_date } = rangeFor(view, anchor);
      const [apptRes, usersRes, exRes] = await Promise.all([
        appointmentApi.getAll({ start_date, end_date, technician_id: technicianId }),
        apiRoot.get('/users', { params: { role: 'technician', status: 'active' } }),
        scheduleApi.getExceptions({ start_date, end_date }),
      ]);
      if (!isCurrent()) return;
      setAppts(segmentAppointments(apptRes));
      setTechs((usersRes.data || []).map((u: any) => ({
        id: u.id,
        name: `${u.first_name} ${u.last_name}`.trim(),
        role: u.role,
        calendar_color: u.calendar_color || undefined,
      })));
      setExceptions(exRes);
    } catch (e: any) {
      if (!isCurrent()) return;
      setError(e?.response?.data?.error || 'Failed to load schedule');
    } finally {
      // Only the newest request owns the spinner — a superseded one finishing
      // must not clear a still-running load's loading state.
      if (isCurrent()) setLoading(false);
    }
  }, [view, anchor, technicianId]);

  useEffect(() => { load(); }, [load]);
  return { appts, techs, exceptions, loading, error, reload: load };
}
